import axios from 'axios';
import type {
    AxiosError,
    AxiosInstance,
    InternalAxiosRequestConfig,
    AxiosResponse
} from 'axios';

// Environment Variables Interface
interface ApiConfig {
    baseURL: string;
    timeout: number;
}

// Get configuration with an intelligent safety guard
const getApiConfig = (): ApiConfig => {
    let envBaseUrl = import.meta.env.VITE_API_BASE_URL;

    // 🔥 THE BULLETPROOF GUARD:
    // If the .env is missing, or caught in the Vite/Next.js cache trap 
    // returning a relative path or the frontend port, we forcefully override it to live.
    if (!envBaseUrl || envBaseUrl === '/api' || envBaseUrl.includes('localhost:3000')) {
        envBaseUrl = 'https://api.pywizard.dev/api';
        console.warn("⚠️ Local .env bypassed. Forcing live backend:", envBaseUrl);
    } else {
        console.log("✅ Using valid .env API URL:", envBaseUrl);
    }

    const timeout = parseInt(import.meta.env.VITE_API_TIMEOUT || '30000');

    return {
        baseURL: envBaseUrl,
        timeout
    };
};

const config = getApiConfig();

export interface ApiError {
    status: number;
    message: string;
    code?: string;
    details?: unknown;
}

export class ApiRequestError extends Error {
    error: ApiError;

    constructor(error: ApiError) {
        super(error.message);
        this.name = 'ApiRequestError';
        this.error = error;
    }
}

interface ApiResponse {
    message?: string;
    error?: string;
    details?: unknown;
}

let currentToken: string | null = null;

// Create axios instance with CORS-friendly configuration
export const axiosInstance: AxiosInstance = axios.create({
    baseURL: config.baseURL,
    timeout: config.timeout,
    withCredentials: true,
    headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
    },
});

// Request interceptor
axiosInstance.interceptors.request.use(
    (config: InternalAxiosRequestConfig) => {
        const token = getCurrentToken();

        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        }

        config.withCredentials = true;

        return config;
    },
    (error: AxiosError) => {
        return Promise.reject(error);
    }
);

// Response interceptor
axiosInstance.interceptors.response.use(
    (response: AxiosResponse) => {
        return response;
    },
    (error: AxiosError<ApiResponse>) => {
        const apiError: ApiError = {
            status: error.response?.status || 0,
            message: error.response?.data?.message || error.message || 'An unknown error occurred',
            code: error.code,
            details: error.response?.data?.details
        };

        // Handle specific error cases
        if (apiError.status === 401) {
            setAuthToken(null);
        }

        const details = apiError.details as any;
        if (apiError.status == 403 && details?.code === 'MFA_REQUIRED') {
            try {
                localStorage.removeItem("mfa_verified");

                window.dispatchEvent(
                    new CustomEvent("mfa-required", {
                        detail: details
                    })
                );

            } catch (err) {
                console.error("Error handling MFA_REQUIRED response:", err);
            }
        }

        return Promise.reject(new ApiRequestError(apiError));
    }
);

export const setAuthToken = (token: string | null): void => {
    currentToken = token;

    if (token) {
        (window as any).__CLERK_TOKEN__ = token;
    } else {
        delete (window as any).__CLERK_TOKEN__;
    }
};

export const getCurrentToken = (): string | null => {
    return currentToken || (window as any).__CLERK_TOKEN__ || null;
};

export const hasValidToken = (): boolean => {
    return !!getCurrentToken();
};

export const isAuthenticatedRequest = (url: string): boolean => {
    const publicEndpoints = [
        '/health',
        '/status',
    ];

    return !publicEndpoints.some(endpoint => url.includes(endpoint));
};

export default axiosInstance;